import React from 'react';
import { z } from 'zod';
import { useFormContext } from 'react-hook-form';
import { QuestionData } from '../types';

interface TextareaFieldProps {
  question: QuestionData;
  placeholder?: string;
  disabled?: boolean;
}

const TextareaFieldComponent: React.FC<TextareaFieldProps> = ({
  question,
  placeholder,
  disabled,
}) => {
  const { register } = useFormContext();
  return (
    <textarea
      {...register(question.slug)}
      placeholder={placeholder}
      className="w-full min-h-[100px] flex rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      rows={4}
      disabled={disabled}
    />
  );
};

const createTextareaSchema = (question: QuestionData): z.ZodTypeAny => {
  let schema = z.string();
  const v = question.validations || {};

  if (v.minLength) {
    schema = schema.min(
      v.minLength,
      `Minimum ${v.minLength} characters required`
    );
  }
  if (v.maxLength) {
    schema = schema.max(
      v.maxLength,
      `Maximum ${v.maxLength} characters allowed`
    );
  }

  // Handle required validation
  if (question.required) {
    // If we already have a minLength, don't add another min(1)
    if (!v.minLength) {
      schema = schema.min(1, 'This field is required');
    }
    return schema;
  }

  return schema.optional();
};

const getDefaultValue = (): string => '';

export const TextareaField = {
  component: TextareaFieldComponent,
  createSchema: createTextareaSchema,
  getDefaultValue: () => getDefaultValue(),
};
